const getSettingsUsersContent = () => {
  return `
    <h2 id="what-is">Što su korisnici i privilegije?</h2>
    <p><strong>Korisnici</strong> su osobe koje imaju pristup AI Kasa sustavu. <strong>Privilegije</strong> određuju što svaki korisnik može vidjeti i raditi u sustavu (pristup blagajni, skladištu, izvještajima, itd.).</p>

    <h2 id="when-to-use" class="mt-6">Kada kreirati korisnike?</h2>
    <ul>
      <li>Pri upisu novog zaposlenika koji će raditi na blagajni</li>
      <li>Dodavanje menadžera koji prati izvještaje</li>
      <li>Dodavanje računovođe za pristup financijskim podacima</li>
      <li>Kreiranje administratorskog pristupa za IT podršku</li>
    </ul>

    <h2 id="prerequisites" class="mt-6">Preduvjeti</h2>
    <ul>
      <li>Pristup "Postavke" → "Korisnici"</li>
      <li>Administratorske privilegije</li>
      <li>Email adresa novog korisnika</li>
    </ul>

    <h2 id="step-by-step" class="mt-6">Korak po korak - Dodavanje korisnika</h2>

    <h3 class="mt-4">1. Kreiranje novog korisnika</h3>
    <ol>
      <li>Idite na <strong>"Postavke" → "Korisnici"</strong></li>
      <li>Kliknite <strong>"+ Dodaj korisnika"</strong></li>
      <li>Otvara se forma za unos</li>
    </ol>

    <h3 class="mt-4">2. Osnovni podaci</h3>
    <table class="info-table">
      <thead>
        <tr>
          <th>Polje</th>
          <th>Obavezno?</th>
          <th>Opis</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Ime</strong></td>
          <td><span class="status-chip error" style="color: white;">DA</span></td>
          <td>Puno ime korisnika</td>
        </tr>
        <tr>
          <td><strong>Email</strong></td>
          <td><span class="status-chip error" style="color: white;">DA</span></td>
          <td>Jedinstvena email adresa za prijavu</td>
        </tr>
        <tr>
          <td><strong>PIN</strong></td>
          <td><span class="status-chip warning" style="color: white;">PREPORUČENO</span></td>
          <td>4-znamenkasti PIN za brzu prijavu na blagajni</td>
        </tr>
        <tr>
          <td><strong>Uloga</strong></td>
          <td><span class="status-chip error" style="color: white;">DA</span></td>
          <td>Administrator, Menadžer, Blagajnik, itd.</td>
        </tr>
        <tr>
          <td><strong>Lozinka</strong></td>
          <td><span class="status-chip grey" style="color: white;">NE</span></td>
          <td>Poslati će se email za postavljanje lozinke</td>
        </tr>
      </tbody>
    </table>

    <h3 class="mt-4">3. Odabir uloge i privilegija</h3>
    <p>Uloge određuju što korisnik može raditi:</p>
    <table class="info-table">
      <thead>
        <tr>
          <th>Uloga</th>
          <th>Pristup</th>
          <th>Primjena</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Administrator</strong></td>
          <td>Potpuni pristup svim modulima i postavkama</td>
          <td>Vlasnik, IT administrator</td>
        </tr>
        <tr>
          <td><strong>Menadžer</strong></td>
          <td>Izvještaji, pregled prodaje, skladište</td>
          <td>Poslovođa, voditelj</td>
        </tr>
        <tr>
          <td><strong>Blagajnik</strong></td>
          <td>Samo rad na blagajni, bez izvještaja</td>
          <td>Konobar, prodavač, radnik na blagajni</td>
        </tr>
        <tr>
          <td><strong>Skladištar</strong></td>
          <td>Skladište: primke, izdatnice, inventura</td>
          <td>Osoba zadužena za skladište</td>
        </tr>
        <tr>
          <td><strong>Računovođa</strong></td>
          <td>Izvještaji, fiskalni podaci (samo čitanje)</td>
          <td>Vanjski računovođa</td>
        </tr>
      </tbody>
    </table>

    <h3 class="mt-4">4. PIN za brzu prijavu</h3>
    <p>PIN je 4-znamenkasti broj za brzu prijavu na blagajni:</p>
    <ol>
      <li>Unesite 4 znamenke (npr. 1234, 9876)</li>
      <li>PIN mora biti jedinstven za svakog korisnika</li>
      <li>Korisnik može promijeniti PIN u svom profilu</li>
    </ol>

    <div class="warning-box">
      <i class="mdi mdi-alert" style="color: #FF9800; margin-right: 8px; font-size: 20px;"></i>
      <div>
        <strong>Sigurnost:</strong> Izbjegavajte očite PINove (0000, 1234, 1111). Preporučamo random brojeve.
      </div>
    </div>

    <h3 class="mt-4">5. Spremanje i slanje pozivnice</h3>
    <ol>
      <li>Kliknite <strong>"Spremi"</strong></li>
      <li>Sustav šalje email korisniku s poveznicom za postavljanje lozinke</li>
      <li>Korisnik aktivira account klikom na link</li>
      <li>Status: <span class="status-chip warning" style="color: white;">Čeka aktivaciju</span> → <span class="status-chip success" style="color: white;">Aktivan</span></li>
    </ol>

    <h2 id="permissions" class="mt-6">Napredne privilegije</h2>
    <p>Za detaljnu kontrolu, možete postaviti granularne privilegije:</p>
    <ul>
      <li><strong>Blagajna:</strong> Otvaranje smjene, izdavanje računa, storno, zatvaranje</li>
      <li><strong>Skladište:</strong> Primke, izdatnice, međuskladišnice, inventura</li>
      <li><strong>Izvještaji:</strong> Prodaja, financijski, skladište, analitika</li>
      <li><strong>Postavke:</strong> Korisnici, certifikati, artikli, cjenik</li>
      <li><strong>Akcije:</strong> Brisanje, storno, odobrenje popusta</li>
    </ul>

    <h2 id="tips" class="mt-6">Savjeti i trikovi</h2>
    <ul>
      <li><strong>Princip najmanje privilegije:</strong> Dajte samo potreban pristup</li>
      <li><strong>Testiranje:</strong> Testirajte nove korisnike prije davanja realnog pristupa</li>
      <li><strong>Dokumentacija:</strong> Vodite listu korisnika i njihovih uloga</li>
      <li><strong>Deaktivacija:</strong> Ne brišite korisnike, deaktivirajte ih (čuva povijest)</li>
      <li><strong>PIN sigurnost:</strong> Mijenjajte PINove povremeno</li>
    </ul>

    <h2 id="troubleshooting" class="mt-6">Što ako...?</h2>

    <details>
      <summary>Korisnik nije dobio email s pozivnicom?</summary>
      <ol>
        <li>Provjerite je li email adresa ispravna</li>
        <li>Provjerite spam folder</li>
        <li>U postavkama korisnika kliknite <strong>"Pošalji ponovo pozivnicu"</strong></li>
        <li>Ako ni dalje ne radi, kontaktirajte podršku</li>
      </ol>
    </details>

    <details>
      <summary>Kako promijeniti privilegije postojećeg korisnika?</summary>
      <ol>
        <li>Idite na "Postavke" → "Korisnici"</li>
        <li>Pronađite korisnika</li>
        <li>Kliknite ikonu olovke <i class="mdi mdi-pencil" style="color: #3989C9; margin: 0 4px; font-size: 16px;"></i></li>
        <li>Promijenite ulogu ili pojedinačne privilegije</li>
        <li>Spremite</li>
      </ol>
      <p>Promjene su odmah aktivne.</p>
    </details>

    <details>
      <summary>Korisnik je zaboravio PIN?</summary>
      <p>Administrator može resetirati PIN:</p>
      <ol>
        <li>Otvorite korisnika</li>
        <li>Kliknite <strong>"Resetiraj PIN"</strong></li>
        <li>Unesite novi PIN</li>
        <li>Obavijestite korisnika o novom PINu</li>
      </ol>
    </details>

    <details>
      <summary>Mogu li obrisati korisnika?</summary>
      <p>Brisanje nije preporučeno:</p>
      <ul>
        <li><i class="mdi mdi-close" style="color: #F44336; margin-right: 4px;"></i> Brisanje uklanja korisnika iz povijesti računa i izvještaja</li>
        <li><i class="mdi mdi-check" style="color: #4CAF50; margin-right: 4px;"></i> <strong>Preporučeno:</strong> Deaktivirajte korisnika (status <span class="status-chip grey" style="color: white;">Neaktivan</span>)</li>
      </ul>
      <p>Deaktivirani korisnik ne može se prijaviti, ali ostaje u povijesti.</p>
    </details>

    <details>
      <summary>Što ako zaposlenik napusti firmu?</summary>
      <ol>
        <li>Odmah <strong>deaktivirajte</strong> korisnika</li>
        <li>Promijenite PIN-ove ostalih korisnika ako je potrebno</li>
        <li>Provjerite da li je bilo neovlaštenih pristupa</li>
        <li>Arhivirajte korisnika u listu bivših zaposlenika</li>
      </ol>
    </details>
  `
}

const getSettingsCertificatesContent = () => {
  return `
    <h2 id="what-is">Što su certifikati i fiskalizacija?</h2>
    <p><strong>Certifikat</strong> je digitalna datoteka (izdana od FINA-e) koja omogućava fiskalizaciju računa. <strong>Fiskalizacija</strong> je zakonom propisana obaveza slanja podataka o računima Poreznoj upravi u realnom vremenu.</p>

    <div class="warning-box">
      <i class="mdi mdi-alert" style="color: #FF9800; margin-right: 8px; font-size: 20px;"></i>
      <strong>Važno za RH:</strong> Svi računi moraju biti fiskalizirani! Bez certifikata ne možete legalno izdavati račune.
    </div>

    <h2 id="when-to-use" class="mt-6">Kada učitati certifikat?</h2>
    <ul>
      <li>Pri prvom postavljanju sustava</li>
      <li>Kada istekne stari certifikat (svake 2 godine)</li>
      <li>Pri dodavanju nove poslovne jedinice</li>
      <li>Nakon promjene tvrtke ili OIB-a</li>
    </ul>

    <h2 id="prerequisites" class="mt-6">Preduvjeti</h2>
    <ul>
      <li>Certifikat dobiven od FINA-e (datoteka .pfx ili .p12)</li>
      <li>Lozinka za certifikat</li>
      <li>Pristup "Postavke" → "Certifikati"</li>
      <li>Administratorske privilegije</li>
    </ul>

    <h2 id="step-by-step" class="mt-6">Korak po korak - Učitavanje certifikata</h2>

    <h3 class="mt-4">1. Priprema certifikata</h3>
    <p>FINA certifikat dolazi u obliku datoteke:</p>
    <ul>
      <li><strong>Format:</strong> .pfx ili .p12</li>
      <li><strong>Naziv:</strong> Obično nešto poput "fiskal-certifikat-2024.pfx"</li>
      <li><strong>Lozinka:</strong> Dobivena od FINA-e (8-16 znakova)</li>
      <li><strong>Rok trajanja:</strong> 2 godine</li>
    </ul>

    <h3 class="mt-4">2. Upload certifikata</h3>
    <ol>
      <li>Idite na <strong>"Postavke" → "Certifikati"</strong></li>
      <li>Kliknite <strong>"+ Učitaj certifikat"</strong></li>
      <li>Odaberite lokaciju (prodajno mjesto)</li>
      <li>Kliknite <strong>"Odaberi datoteku"</strong> i odaberite .pfx/.p12 fajl</li>
      <li>Unesite <strong>lozinku certifikata</strong></li>
      <li>Kliknite <strong>"Upload"</strong></li>
    </ol>

    <h3 class="mt-4">3. Verifikacija certifikata</h3>
    <p>Sustav automatski provjerava:</p>
    <table class="info-table">
      <thead>
        <tr>
          <th>Provjera</th>
          <th>Što se provjerava</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><span class="status-chip success" style="color: white;">Valjan</span></td>
          <td>Certifikat je ispravan i može se koristiti</td>
        </tr>
        <tr>
          <td><span class="status-chip warning" style="color: white;">Ističe uskoro</span></td>
          <td>Certifikat vrijedi još < 30 dana</td>
        </tr>
        <tr>
          <td><span class="status-chip error" style="color: white;">Istekao</span></td>
          <td>Certifikat više nije valjan</td>
        </tr>
        <tr>
          <td><span class="status-chip grey" style="color: white;">Neaktivan</span></td>
          <td>Certifikat nije u upotrebi</td>
        </tr>
      </tbody>
    </table>

    <h3 class="mt-4">4. Testiranje fiskalizacije</h3>
    <ol>
      <li>Kliknite <strong>"Test fiskalizacije"</strong></li>
      <li>Sustav šalje testni račun Poreznoj upravi</li>
      <li>Rezultat:
        <ul>
          <li><span class="status-chip success" style="color: white;">Uspješno</span> - Sve je u redu, možete početi koristiti</li>
          <li><span class="status-chip error" style="color: white;">Greška</span> - Vidi troubleshooting ispod</li>
        </ul>
      </li>
    </ol>

    <div class="success-box">
      <i class="mdi mdi-lightbulb" style="color: #4CAF50; margin-right: 8px; font-size: 20px;"></i>
      <div>
        <strong>Savjet:</strong> Testirajte fiskalizaciju prije nego krenete s normalnim radom!
      </div>
    </div>

    <h2 id="fiscal-codes" class="mt-6">Važni pojmovi</h2>
    <table class="info-table">
      <thead>
        <tr>
          <th>Pojam</th>
          <th>Objašnjenje</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>JIR</strong></td>
          <td>Jedinstveni Identifikator Računa - broj koji dobivate od Porezne za svaki račun</td>
        </tr>
        <tr>
          <td><strong>ZKI</strong></td>
          <td>Zaštitni Kod Izdavatelja - QR kod na računu</td>
        </tr>
        <tr>
          <td><strong>OIB</strong></td>
          <td>Osobni Identifikacijski Broj tvrtke (11 znamenki)</td>
        </tr>
        <tr>
          <td><strong>Poslovni prostor</strong></td>
          <td>Fizička lokacija gdje izdajete račune</td>
        </tr>
        <tr>
          <td><strong>Naplatni uređaj</strong></td>
          <td>Blagajna / uređaj koji izdaje račune</td>
        </tr>
      </tbody>
    </table>

    <h2 id="tips" class="mt-6">Savjeti i trikovi</h2>
    <ul>
      <li><strong>Praćenje isteka:</strong> Sustav će vas upozoriti 30 dana prije isteka</li>
      <li><strong>Backup:</strong> Spremite .pfx datoteku i lozinku na sigurno mjesto</li>
      <li><strong>Obnova:</strong> Naručite novi certifikat 2 mjeseca prije isteka</li>
      <li><strong>Testiranje:</strong> Testirajte nakon svakog uploada ili promjene</li>
      <li><strong>Podrška:</strong> Za probleme s certifikatom kontaktirajte FINA-u, ne podršku AI Kasa</li>
    </ul>

    <h2 id="troubleshooting" class="mt-6">Što ako...?</h2>

    <details>
      <summary>Greška "Neispravna lozinka"?</summary>
      <p>Lozinka koju ste unijeli ne odgovara certifikatu:</p>
      <ol>
        <li>Provjerite je li lozinka točna (obratite se FINA-i)</li>
        <li>Kopirajte lozinku iz dokumentacije (izbjegavajte tipkanje)</li>
        <li>Provjerite je li Caps Lock uključen</li>
      </ol>
    </details>

    <details>
      <summary>Test fiskalizacije vraća grešku "Connection timeout"?</summary>
      <p>Problem s mrežom ili Poreznom upravom:</p>
      <ul>
        <li>Provjerite internetsku vezu</li>
        <li>Pokušajte ponovo nakon nekoliko minuta</li>
        <li>Provjerite je li vatrozid blokira vezu prema cis.porezna-uprava.hr</li>
        <li>Kontaktirajte vašeg IT administratora</li>
      </ul>
    </details>

    <details>
      <summary>Certifikat je istekao - što sad?</summary>
      <p>Hitno nabavite novi certifikat:</p>
      <ol>
        <li>Kontaktirajte FINA-u za izdavanje novog certifikata</li>
        <li>Proces obično traje 3-5 radnih dana</li>
        <li><strong>Dok čekate:</strong> NE MOŽETE izdavati fiskalizirane račune!</li>
        <li>Kada dobijete novi, učitajte ga odmah</li>
      </ol>
      <div class="error-box">
        <i class="mdi mdi-alert-circle" style="color: #F44336; margin-right: 8px; font-size: 20px;"></i>
        <strong>Važno:</strong> Izdavanje računa bez valjanog certifikata je kazneno djelo!
      </div>
    </details>

    <details>
      <summary>Kako provjeriti datum isteka certifikata?</summary>
      <ol>
        <li>Idite na "Postavke" → "Certifikati"</li>
        <li>Vidjet ćete tablicu sa svim certifikatima</li>
        <li>Kolona "Vrijedi do" pokazuje rok</li>
        <li>Ako je manje od 30 dana, status je <span class="status-chip warning" style="color: white;">Ističe uskoro</span></li>
      </ol>
    </details>

    <details>
      <summary>Mogu li imati više certifikata?</summary>
      <p>Da, ako imate više lokacija ili poslovnih jedinica:</p>
      <ul>
        <li>Svaka lokacija može imati zasebni certifikat</li>
        <li>Ili možete koristiti jedan certifikat za sve lokacije</li>
        <li>Ovisno o tome kako ste registrirali poslovne prostore u FINA-i</li>
      </ul>
    </details>

    <details>
      <summary>Što je razlika između produkcijskog i demo certifikata?</summary>
      <table class="info-table">
        <thead>
          <tr>
            <th>Tip</th>
            <th>Namjena</th>
            <th>Gdje koristiti</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Demo certifikat</strong></td>
            <td>Za testiranje i uvježbavanje</td>
            <td>Testno okruženje, obuka osoblja</td>
          </tr>
          <tr>
            <td><strong>Produkcijski certifikat</strong></td>
            <td>Za pravu prodaju</td>
            <td>Stvarni računi kupcima</td>
          </tr>
        </tbody>
      </table>
      <p><strong>Nikad ne koristite demo certifikat za prave račune!</strong></p>
    </details>
  `
}

const getSettingsCommunicationContent = () => {
  return `
    <h2 id="communication-settings">Komunikacija i obavijesti</h2>
    <p>
      Konfigurirajte načine komunikacije i automatske obavijesti za različite događaje u sustavu.
    </p>

    <h3 class="mt-4">Tipovi obavijesti</h3>

    <table class="info-table">
      <thead>
        <tr>
          <th>Tip</th>
          <th>Opis</th>
          <th>Primjena</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Email</strong></td>
          <td>Slanje obavijesti putem elektronske pošte</td>
          <td>Izvještaji, potvrde narudžbi, računi</td>
        </tr>
        <tr>
          <td><strong>SMS</strong></td>
          <td>Slanje kratkih tekstualnih poruka</td>
          <td>Upozorenja, potvrde, OTP kodovi</td>
        </tr>
        <tr>
          <td><strong>Push obavijesti</strong></td>
          <td>Notifikacije na mobitelu ili tabletu</td>
          <td>Hitne obavijesti, statusni updates</td>
        </tr>
        <tr>
          <td><strong>In-app obavijesti</strong></td>
          <td>Obavijesti unutar aplikacije</td>
          <td>Sistemske poruke, upozorenja</td>
        </tr>
      </tbody>
    </table>

    <h3 class="mt-4">Konfiguracija Email obavijesti</h3>

    <h4 class="mt-3">SMTP postavke</h4>
    <ol>
      <li>Idite na <strong>"Postavke" → "Komunikacija" → "Email"</strong></li>
      <li>Unesite SMTP server (npr. smtp.gmail.com)</li>
      <li>Port (465 za SSL, 587 za TLS)</li>
      <li>Email adresa i lozinka</li>
      <li>Testirajte konfiguraciju</li>
    </ol>

    <div class="info-box">
      <i class="mdi mdi-information" style="color: #3989C9; margin-right: 8px; font-size: 20px;"></i>
      <div>
        <strong>Savjet:</strong> Za Gmail koristite "App Password" umjesto standardne lozinke
        radi povećane sigurnosti.
      </div>
    </div>

    <h3 class="mt-4">Automatske obavijesti</h3>

    <p>Definirajte kada sustav automatski šalje obavijesti:</p>

    <table class="info-table">
      <thead>
        <tr>
          <th>Događaj</th>
          <th>Email</th>
          <th>SMS</th>
          <th>Push</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Nova narudžba</td>
          <td>✓</td>
          <td>✓</td>
          <td>✓</td>
        </tr>
        <tr>
          <td>Gotovina ispod minimuma</td>
          <td>✓</td>
          <td>-</td>
          <td>✓</td>
        </tr>
        <tr>
          <td>Greška u fiskalizaciji</td>
          <td>✓</td>
          <td>✓</td>
          <td>✓</td>
        </tr>
        <tr>
          <td>Dnevni izvještaj</td>
          <td>✓</td>
          <td>-</td>
          <td>-</td>
        </tr>
        <tr>
          <td>Artikl ispod minimuma</td>
          <td>✓</td>
          <td>-</td>
          <td>✓</td>
        </tr>
      </tbody>
    </table>

    <h3 class="mt-4">Konfiguracija SMS obavijesti</h3>

    <p>Za SMS obavijesti potreban je SMS gateway:</p>
    <ul>
      <li><strong>Infobip</strong> - Najpopularniji SMS provider u Hrvatskoj</li>
      <li><strong>Twilio</strong> - Međunarodni SMS servis</li>
      <li><strong>Poruka.hr</strong> - Domaći SMS provider</li>
    </ul>

    <h4 class="mt-3">Postavke</h4>
    <ol>
      <li>Odaberite SMS providera</li>
      <li>Unesite API ključ</li>
      <li>Definirajte ime pošiljatelja (Sender ID)</li>
      <li>Testirajte slanje</li>
    </ol>

    <h3 class="mt-4">Push obavijesti</h3>

    <p>
      Push obavijesti omogućavaju trenutno primanje obavijesti na mobilnim uređajima.
    </p>

    <h4 class="mt-3">Aktivacija</h4>
    <ol>
      <li>Instalirajte AI Kasa mobilnu aplikaciju</li>
      <li>Prijavite se sa svojim računom</li>
      <li>Dozvolite notifikacije kada aplikacija zatraži</li>
      <li>Odaberite tipove obavijesti koje želite primati</li>
    </ol>

    <h3 class="mt-4">Email predlošci</h3>

    <p>Prilagodite email predloške za različite obavijesti:</p>
    <ul>
      <li><strong>Logo</strong> - Dodajte logo tvrtke</li>
      <li><strong>Boje</strong> - Prilagodite boje prema brandu</li>
      <li><strong>Tekst</strong> - Uredite tekst poruke</li>
      <li><strong>Footer</strong> - Dodajte kontakt informacije</li>
    </ul>

    <div class="success-box">
      <i class="mdi mdi-lightbulb" style="color: #4CAF50; margin-right: 8px; font-size: 20px;"></i>
      <div>
        <strong>Savjet:</strong> Testirajte sve obavijesti prije aktivacije kako bi osigurali
        da se poruke ispravno šalju i prikazuju.
      </div>
    </div>

    <h3 class="mt-4">Često postavljana pitanja</h3>

    <h4 class="mt-3">Mogu li onemogućiti neke obavijesti?</h4>
    <p>
      Da, možete individualno omogućiti ili onemogućiti svaku vrstu obavijesti
      u postavkama komunikacije.
    </p>

    <h4 class="mt-3">Koliko koštaju SMS obavijesti?</h4>
    <p>
      Cijena SMS-a ovisi o vašem provideru. Obično je između 0.05 - 0.15 EUR po poruci
      za domaće SMS-ove.
    </p>

    <h4 class="mt-3">Što ako ne primam push obavijesti?</h4>
    <p>
      Provjerite postavke mobilnog uređaja - možda ste blokiral notifikacije za aplikaciju.
      Također, provjerite da je aplikacija ažurirana na najnoviju verziju.
    </p>
  `
}

const getSettingsCompanyContent = () => {
  return `
    <h2 id="what-is">Što su Company Settings?</h2>
    <p><strong>Company Settings</strong> (Postavke tvrtke) sadrže osnovne informacije o vašem poslovanju: podatke o tvrtki, logo, valute, PDV postavke i opće konfiguracije koje utječu na cijeli sustav.</p>

    <h2 id="when-to-use" class="mt-6">Kada konfigurirati?</h2>
    <ul>
      <li>Pri prvom postavljanju sustava</li>
      <li>Kod promjene podataka tvrtke (adresa, OIB, naziv)</li>
      <li>Pri dodavanju novog logoa</li>
      <li>Promjena valute ili PDV postavki</li>
    </ul>

    <h2 id="prerequisites" class="mt-6">Preduvjeti</h2>
    <ul>
      <li>Pristup "Postavke" → "Company"</li>
      <li>Administratorske privilegije</li>
      <li>Logo datoteka (PNG ili JPG, preporučeno 500x500px)</li>
    </ul>

    <h2 id="step-by-step" class="mt-6">Osnovne postavke tvrtke</h2>

    <h3 class="mt-4">1. Podaci o tvrtki</h3>
    <table class="info-table">
      <thead>
        <tr>
          <th>Polje</th>
          <th>Objašnjenje</th>
          <th>Primjer</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Naziv tvrtke</strong></td>
          <td>Službeni naziv koji se prikazuje na računima</td>
          <td>"Restoran Kod Ive d.o.o."</td>
        </tr>
        <tr>
          <td><strong>OIB</strong></td>
          <td>Poreski broj (11 znamenki)</td>
          <td>12345678901</td>
        </tr>
        <tr>
          <td><strong>Adresa</strong></td>
          <td>Puna adresa sjedišta</td>
          <td>"Ilica 123, 10000 Zagreb"</td>
        </tr>
        <tr>
          <td><strong>Telefon</strong></td>
          <td>Kontakt broj</td>
          <td>+385 1 234 5678</td>
        </tr>
        <tr>
          <td><strong>Email</strong></td>
          <td>Kontakt email</td>
          <td>info@restoran.hr</td>
        </tr>
        <tr>
          <td><strong>Web</strong></td>
          <td>Web stranica (opcionalno)</td>
          <td>www.restoran.hr</td>
        </tr>
      </tbody>
    </table>

    <h3 class="mt-4">2. Upload logoa</h3>
    <ol>
      <li>Idite na sekciju <strong>"Logo"</strong></li>
      <li>Kliknite <strong>"Upload Logo"</strong></li>
      <li>Odaberite sliku (PNG ili JPG)</li>
      <li>Preporučena veličina: 500x500px ili kvadratni omjer</li>
      <li>Logo se prikazuje na računima i u aplikaciji</li>
    </ol>

    <div class="success-box">
      <i class="mdi mdi-lightbulb" style="color: #4CAF50; margin-right: 8px; font-size: 20px;"></i>
      <div>
        <strong>Savjet:</strong> Koristite transparentnu PNG sliku za najbolji prikaz na računima.
      </div>
    </div>

    <h3 class="mt-4">3. Postavke valute</h3>
    <table class="info-table">
      <thead>
        <tr>
          <th>Postavka</th>
          <th>Objašnjenje</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Osnovna valuta</strong></td>
          <td>Primarna valuta poslovanja (EUR za RH)</td>
        </tr>
        <tr>
          <td><strong>Dodatne valute</strong></td>
          <td>Opciono: USD, GBP za turističke objekte</td>
        </tr>
        <tr>
          <td><strong>Tečajna lista</strong></td>
          <td>Automatsko ažuriranje tečaja iz HNB</td>
        </tr>
      </tbody>
    </table>

    <h3 class="mt-4">4. PDV postavke</h3>
    <p><i class="mdi mdi-information" style="color: #3989C9; margin-right: 8px; font-size: 20px;"></i> <strong>Važne odluke koje utječu na cijeli sustav:</strong></p>

    <table class="info-table">
      <thead>
        <tr>
          <th>Postavka</th>
          <th>Što znači</th>
          <th>Primjer</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Cijene sadrže PDV</strong></td>
          <td>Da li unosite cijene S ili BEZ PDV-a</td>
          <td>✓ = Unosite 25 kn (s PDV-om)<br>✗ = Unosite 20 kn (sustav dodaje 25%)</td>
        </tr>
        <tr>
          <td><strong>Standardna PDV stopa</strong></td>
          <td>Zadana stopa za nove artikle</td>
          <td>25% (RH standard)</td>
        </tr>
        <tr>
          <td><strong>Snižena PDV stopa</strong></td>
          <td>Za određene kategorije (hrana, knjige)</td>
          <td>13%, 5%</td>
        </tr>
      </tbody>
    </table>

    <div class="warning-box">
      <i class="mdi mdi-alert" style="color: #FF9800; margin-right: 8px; font-size: 20px;"></i>
      <div>
        <strong>Važno:</strong> Promjena "Cijene sadrže PDV" nakon što ste postavili cjenik može uzrokovati probleme! Postavite ovo na početku i ne mijenjajte kasnije.
      </div>
    </div>

    <h3 class="mt-4">5. Opće postavke</h3>
    <ul>
      <li><strong>Vremensko pojasno:</strong> Europe/Zagreb</li>
      <li><strong>Format datuma:</strong> DD.MM.YYYY (HR standard)</li>
      <li><strong>Decimalna točnost:</strong> 2 decimale (za cijene u EUR)</li>
      <li><strong>Jezik:</strong> Hrvatski</li>
    </ul>

    <h2 id="tips" class="mt-6">Savjeti i trikovi</h2>
    <ul>
      <li><strong>Logo kvaliteta:</strong> Koristite visoku rezoluciju za profesionalan izgled</li>
      <li><strong>Provjera podataka:</strong> OIB i adresa moraju odgovarati FINA registraciji</li>
      <li><strong>PDV odluka:</strong> Konsultirajte računovođu prije postavljanja "Cijene sadrže PDV"</li>
      <li><strong>Backup postavki:</strong> Spremite snimku ekrana svih postavki</li>
      <li><strong>Testiranje:</strong> Nakon promjene, testirajte izdavanje računa</li>
    </ul>

    <h2 id="troubleshooting" class="mt-6">Što ako...?</h2>

    <details>
      <summary>Logo se ne prikazuje ispravno na računu?</summary>
      <p>Provjere:</p>
      <ul>
        <li>Veličina slike: max 2MB</li>
        <li>Format: PNG ili JPG (ne BMP, ne GIF)</li>
        <li>Dimenzije: preporučeno kvadratne (500x500px)</li>
        <li>Probajte upload-ati ponovo</li>
        <li>Očistite cache preglednika</li>
      </ul>
    </details>

    <details>
      <summary>Mogu li promijeniti OIB nakon što sam već kreirao artikle?</summary>
      <p>Da, ali:</p>
      <ul>
        <li>OIB se može promijeniti u postavkama</li>
        <li>Postojeći računi zadržavaju stari OIB (povijesni podaci)</li>
        <li>Novi računi će koristiti novi OIB</li>
        <li><strong>Važno:</strong> Kontaktirajte FINA-u za ažuriranje fiskalnih certifikata!</li>
      </ul>
    </details>

    <details>
      <summary>Promijenio sam "Cijene sadrže PDV" i sada su cijene pogrešne?</summary>
      <p>Ova promjena utječe na kalkulacije:</p>
      <ol>
        <li><strong>Ne mijenjajte ovu postavku</strong> nakon što ste postavili cjenik</li>
        <li>Ako ste već promijenili:
          <ul>
            <li>Vratite na staru vrijednost</li>
            <li>Ili pregledajte SVE cijene i ispravite ih</li>
          </ul>
        </li>
        <li>Za siguran prijelaz kontaktirajte podršku</li>
      </ol>
    </details>

    <details>
      <summary>Kako dodati dodatnu valutu (npr. USD za turiste)?</summary>
      <ol>
        <li>Idite na "Postavke" → "Company" → "Valute"</li>
        <li>Kliknite "+ Dodaj valutu"</li>
        <li>Odaberite USD (ili drugu valutu)</li>
        <li>Postavite način konverzije:
          <ul>
            <li>Automatski tečaj (HNB)</li>
            <li>Ili fiksni tečaj</li>
          </ul>
        </li>
        <li>Spremite</li>
        <li>Na blagajni će biti dostupan odabir valute</li>
      </ol>
    </details>

    <details>
      <summary>Što su "Lokacije" vs "Poslovne jedinice"?</summary>
      <table class="info-table">
        <thead>
          <tr>
            <th>Koncept</th>
            <th>Objašnjenje</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Lokacija</strong></td>
            <td>Fizička adresa gdje poslujete (restoran, prodavaonica)</td>
          </tr>
          <tr>
            <td><strong>Poslovna jedinica</strong></td>
            <td>Organizacijska podjela (kuhinja, šank, skladište na istoj lokaciji)</td>
          </tr>
          <tr>
            <td><strong>Company</strong></td>
            <td>Nadređena tvrtka koja sadrži sve lokacije</td>
          </tr>
        </tbody>
      </table>
    </details>
  `
}

const getSettingsFiskalizacijaEracunContent = () => {
  return `
    <h2 id="what-is">Što je Fiskalizacija i eRacun?</h2>
    <p><strong>Fiskalizacija</strong> je opcija na računu koja određuje hoće li račun biti poslan Poreznoj upravi (fiskaliziran). <strong>eRacun</strong> (elektronički račun) je sustav za slanje računa kupcima putem e-maila u elektroničkom formatu.</p>

    <div class="info-box">
      <i class="mdi mdi-information" style="color: #3989C9; margin-right: 8px; font-size: 20px;"></i>
      <div>
        <strong>Važno:</strong> Fiskalizacija i eRacun su međusobno isključivi - ako je fiskalizacija uključena (Da), eRacun nije dostupan.
      </div>
    </div>

    <h2 id="when-to-use" class="mt-6">Kada koristiti?</h2>

    <h3 class="mt-4">Fiskalizacija</h3>
    <table class="info-table">
      <thead>
        <tr>
          <th>Opcija</th>
          <th>Kada koristiti</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Da</strong></td>
          <td>Standardni računi za kupce, zakonska obaveza, svi transakcijski računi</td>
        </tr>
        <tr>
          <td><strong>Ne</strong></td>
          <td>Interni računi, predračuni, proslijeđivanje eRačuna</td>
        </tr>
      </tbody>
    </table>

    <h3 class="mt-4">eRacun</h3>
    <ul>
      <li>Slanje računa pravnim osobama u Hrvatskoj</li>
      <li>Dostava računa putem e-maila</li>
      <li>Računi koji neće biti fiskalizirani</li>
      <li>B2B transakcije s kompletnim podacima kupca</li>
    </ul>

    <h2 id="prerequisites" class="mt-6">Preduvjeti za eRacun</h2>
    <p>eRacun je dostupan samo ako su ispunjeni SVI sljedeći uvjeti:</p>
    <table class="info-table">
      <thead>
        <tr>
          <th>Uvjet</th>
          <th>Obavezno?</th>
          <th>Opis</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Fiskalizacija</strong></td>
          <td><span class="status-chip error" style="color: white;">DA</span></td>
          <td>Mora biti postavljena na "Ne"</td>
        </tr>
        <tr>
          <td><strong>Tip računa</strong></td>
          <td><span class="status-chip error" style="color: white;">DA</span></td>
          <td>Samo transakcijski računi (T)</td>
        </tr>
        <tr>
          <td><strong>Ime/OIB kupca</strong></td>
          <td><span class="status-chip error" style="color: white;">DA</span></td>
          <td>Obavezno popunjeno</td>
        </tr>
        <tr>
          <td><strong>Adresa</strong></td>
          <td><span class="status-chip error" style="color: white;">DA</span></td>
          <td>Adresa kupca</td>
        </tr>
        <tr>
          <td><strong>Grad</strong></td>
          <td><span class="status-chip error" style="color: white;">DA</span></td>
          <td>Grad kupca</td>
        </tr>
        <tr>
          <td><strong>Država</strong></td>
          <td><span class="status-chip error" style="color: white;">DA</span></td>
          <td>Mora biti "Hrvatska"</td>
        </tr>
        <tr>
          <td><strong>KPD Šifre</strong></td>
          <td><span class="status-chip error" style="color: white;">DA</span></td>
          <td>Sve stavke moraju imati KPD šifru</td>
        </tr>
        <tr>
          <td><strong>Email</strong></td>
          <td><span class="status-chip error" style="color: white;">DA</span></td>
          <td>Validna email adresa za dostavu</td>
        </tr>
      </tbody>
    </table>

    <h2 id="step-by-step" class="mt-6">Korak po korak</h2>

    <h3 class="mt-4">1. Postavljanje fiskalizacije na računu</h3>
    <p>Fiskalizacija se može postaviti na više mjesta u sustavu:</p>

    <h4 class="mt-3">Opcija A: Pri uređivanju računa</h4>
    <ol>
      <li>Otvorite račun za uređivanje (<strong>EditReceiptDialog</strong>)</li>
      <li>Pronađite dropdown "Fiskalizacija" na vrhu forme</li>
      <li>Odaberite:
        <ul>
          <li><span class="status-chip success" style="color: white;">Da</span> - Račun će biti fiskaliziran</li>
          <li><span class="status-chip warning" style="color: white;">Ne</span> - Račun neće biti fiskaliziran</li>
        </ul>
      </li>
    </ol>

    <h4 class="mt-3">Opcija B: Pri izdavanju računa (Wizard)</h4>
    <ol>
      <li>U koraku <strong>Način plaćanja</strong> (PaymentModeSelection)</li>
      <li>Vidjet ćete karticu sa opcijama fiskalizacije</li>
      <li>Odaberite željenu opciju</li>
    </ol>

    <h3 class="mt-4">2. Aktivacija eRacuna</h3>
    <p>Kada su ispunjeni svi preduvjeti, možete aktivirati eRacun:</p>

    <ol>
      <li>Provjerite da je fiskalizacija postavljena na <strong>"Ne"</strong></li>
      <li>Unesite sve podatke o kupcu:
        <ul>
          <li>Ime ili OIB</li>
          <li>Adresa</li>
          <li>Grad</li>
          <li>Država (mora biti "Hrvatska")</li>
        </ul>
      </li>
      <li>Provjerite da sve stavke na računu imaju <strong>KPD Šifru</strong></li>
      <li>Označite checkbox <strong>"eRačun"</strong></li>
      <li>Unesite validnu <strong>email adresu</strong> za dostavu</li>
    </ol>

    <div class="success-box">
      <i class="mdi mdi-lightbulb" style="color: #4CAF50; margin-right: 8px; font-size: 20px;"></i>
      <div>
        <strong>Savjet:</strong> Ako je eRačun checkbox onemogućen (siv), provjerite jesu li ispunjeni svi preduvjeti u gornjem popisu. Sustav automatski provjerava sve uvjete.
      </div>
    </div>

    <h3 class="mt-4">3. Provjera KPD šifri</h3>
    <p>eRacun zahtijeva da sve stavke na računu imaju KPD šifru:</p>
    <ol>
      <li>Pregledajte sve stavke u košarici/računu</li>
      <li>Provjerite kolonu <strong>"KPD Šifra"</strong> (nalazi se nakon "Popust%")</li>
      <li>Ako neka stavka nema KPD šifru:
        <ul>
          <li>Kliknite u polje KPD Šifra za tu stavku</li>
          <li>Unesite odgovarajuću KPD šifru (npr. "10", "20", "30", itd.)</li>
        </ul>
      </li>
      <li>Sve stavke moraju imati popunjenu KPD šifru za aktivaciju eRacuna</li>
    </ol>

    <div class="info-box">
      <i class="mdi mdi-information" style="color: #3989C9; margin-right: 8px; font-size: 20px;"></i>
      <div>
        Za više informacija o KPD šiframa, pogledajte <strong>Artikli → KPD Šifra</strong> u ovom priručniku.
      </div>
    </div>

    <h3 class="mt-4">4. Validacija email adrese</h3>
    <p>Email adresa za eRacun mora biti u ispravnom formatu:</p>
    <ul>
      <li>Mora sadržavati @ znak</li>
      <li>Mora imati domenu (.hr, .com, itd.)</li>
      <li>Primjeri ispravnih adresa: info@tvrtka.hr, kupac@email.com</li>
      <li>Sustav će prikazati grešku ako format nije ispravan</li>
    </ul>

    <h3 class="mt-4">5. Slanje računa s eRacunom</h3>
    <ol>
      <li>Kada su svi uvjeti ispunjeni, checkbox eRacun je aktivan</li>
      <li>Označite checkbox i unesite email</li>
      <li>Kliknite <strong>"Kreiraj račun"</strong> ili <strong>"Spremi"</strong></li>
      <li>Sustav šalje račun s parametrima:
        <ul>
          <li><code>eracun: true</code></li>
          <li><code>eracun_email: uneseni@email.com</code></li>
        </ul>
      </li>
      <li>Kupac prima eRacun na navedenu email adresu</li>
    </ol>

    <h2 id="validation" class="mt-6">Pravila validacije</h2>
    <p>Sustav automatski provjerava uvjete za eRacun u realnom vremenu:</p>

    <table class="info-table">
      <thead>
        <tr>
          <th>Uvjet</th>
          <th>Status ako nije ispunjeno</th>
          <th>Rješenje</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Fiskalizacija = "Ne"</td>
          <td>eRacun checkbox onemogućen</td>
          <td>Postavite fiskalizaciju na "Ne"</td>
        </tr>
        <tr>
          <td>Kompletni podaci kupca</td>
          <td>eRacun checkbox onemogućen</td>
          <td>Popunite ime/OIB, adresu, grad, državu</td>
        </tr>
        <tr>
          <td>Država = "Hrvatska"</td>
          <td>eRacun checkbox onemogućen</td>
          <td>Odaberite "Hrvatska" u polju država</td>
        </tr>
        <tr>
          <td>Sve stavke imaju KPD šifru</td>
          <td>eRacun checkbox onemogućen</td>
          <td>Dodajte KPD šifre svim stavkama</td>
        </tr>
        <tr>
          <td>Validan email format</td>
          <td>Greška pri spremanju</td>
          <td>Unesite ispravan email (ime@domena.hr)</td>
        </tr>
      </tbody>
    </table>

    <h2 id="tips" class="mt-6">Savjeti i trikovi</h2>
    <ul>
      <li><strong>Status indikatori:</strong> Sustav prikazuje <span class="status-chip success" style="color: white;">Fiskalizirano</span> ili <span class="status-chip warning" style="color: white;">Nefiskalizirano</span> chipove za brzu identifikaciju</li>
      <li><strong>Brza provjera:</strong> Ako eRacun checkbox nije dostupan, pregledajte poruku ispod checkboxa - sustav prikazuje što nedostaje</li>
      <li><strong>Predlošci kupaca:</strong> Kreirajte predloške s kompletnim podacima za česte kupce B2B transakcija</li>
      <li><strong>KPD šifre po defaultu:</strong> Postavite KPD šifre na artikle u cjeniku, ne unositi ih ručno svaki put</li>
      <li><strong>Email adrese:</strong> Vodite listu email adresa za stalne kupce za brže ispunjavanje</li>
      <li><strong>Testiranje:</strong> Testirajte slanje eRacuna na vlastiti email prije korištenja sa pravim kupcima</li>
    </ul>

    <h2 id="troubleshooting" class="mt-6">Što ako...?</h2>

    <details>
      <summary>eRacun checkbox je onemogućen (siv)?</summary>
      <p>Sustav automatski onemogućava eRacun ako nisu ispunjeni svi preduvjeti. Provjerite redom:</p>
      <ol>
        <li><strong>Fiskalizacija:</strong> Mora biti postavljena na "Ne"</li>
        <li><strong>Tip računa:</strong> Mora biti transakcijski (T), ne predračun ili storno</li>
        <li><strong>Podaci kupca:</strong>
          <ul>
            <li>Ime ili OIB: popunjeno</li>
            <li>Adresa: popunjena</li>
            <li>Grad: popunjen</li>
            <li>Država: "Hrvatska"</li>
          </ul>
        </li>
        <li><strong>KPD šifre:</strong> Sve stavke na računu moraju imati KPD šifru</li>
      </ol>
      <p>Sustav obično prikazuje poruku ispod checkboxa s objašnjenjem što nedostaje.</p>
    </details>

    <details>
      <summary>Greška "Email nije validan"?</summary>
      <p>Email adresa mora biti u ispravnom formatu:</p>
      <ul>
        <li>Provjerite ima li @ znak (npr. info@tvrtka.hr)</li>
        <li>Provjerite ima li domenu nakon @ (npr. .hr, .com)</li>
        <li>Uklonite razmake prije i nakon email adrese</li>
        <li>Ne koristite hrvatske znakove (č, ć, š) u email adresi</li>
      </ul>
      <p><strong>Primjeri ispravnih emailova:</strong> kupac@firma.hr, info@email.com</p>
      <p><strong>Primjeri neispravnih emailova:</strong> kupac@, @firma.hr, kupac firma.hr</p>
    </details>

    <details>
      <summary>Mogu li koristiti fiskalizaciju i eRacun zajedno?</summary>
      <p>Ne, fiskalizacija i eRacun su međusobno isključivi:</p>
      <ul>
        <li><i class="mdi mdi-close" style="color: #F44336; margin-right: 4px;"></i> Fiskalizacija "Da" + eRacun - nije moguće</li>
        <li><i class="mdi mdi-check" style="color: #4CAF50; margin-right: 4px;"></i> Fiskalizacija "Ne" + eRacun - moguće</li>
      </ul>
      <div class="warning-box">
        <i class="mdi mdi-alert" style="color: #FF9800; margin-right: 8px; font-size: 20px;"></i>
        <div>
          <strong>Razlog:</strong> Fiskalizirani računi se šalju Poreznoj upravi standardnim načinom, dok eRacuni koriste drugačiji format i kanal dostave.
        </div>
      </div>
    </details>

    <details>
      <summary>Kupac nema sve podatke (nedostaje adresa ili grad)?</summary>
      <p>eRacun zahtijeva kompletne podatke kupca:</p>
      <ol>
        <li>Zatražite od kupca kompletne podatke (adresa, grad)</li>
        <li>Ili koristite standardnu fiskalizaciju ("Da") bez eRacuna</li>
        <li>Za strane kupce (izvan RH), eRacun nije dostupan</li>
      </ol>
      <p><strong>Napomena:</strong> Za pravne osobe (tvrtke), ovi podaci su obavezni po zakonu za B2B transakcije.</p>
    </details>

    <details>
      <summary>Što ako neka stavka nema KPD šifru?</summary>
      <p>Sve stavke moraju imati KPD šifru za eRacun:</p>
      <ol>
        <li>Pregledajte kolonu "KPD Šifra" u računu</li>
        <li>Za svaku stavku bez šifre:
          <ul>
            <li>Kliknite u polje KPD Šifra</li>
            <li>Unesite odgovarajuću šifru (pogledajte Artikli → KPD Šifra u priručniku)</li>
          </ul>
        </li>
        <li>Ili postavite KPD šifre direktno na artikle u cjeniku za buduću upotrebu</li>
      </ol>
      <p><strong>Česte KPD šifre:</strong></p>
      <ul>
        <li>10 - Gotova roba</li>
        <li>20 - Usluge</li>
        <li>30 - Trgovačka roba</li>
      </ul>
    </details>

    <details>
      <summary>Kupac nije dobio eRacun na email?</summary>
      <p>Mogući uzroci i rješenja:</p>
      <ol>
        <li><strong>Provjera email adrese:</strong>
          <ul>
            <li>Je li email adresa ispravno unesena?</li>
            <li>Provjerite tipfeler u email adresi</li>
          </ul>
        </li>
        <li><strong>Spam folder:</strong>
          <ul>
            <li>Zamolite kupca da provjeri spam/junk folder</li>
            <li>Dodajte pošiljatelja u whitelist</li>
          </ul>
        </li>
        <li><strong>Kašnjenje dostave:</strong>
          <ul>
            <li>Email može stići s malim kašnjenjem (1-5 minuta)</li>
            <li>Pričekajte nekoliko minuta prije ponovnog slanja</li>
          </ul>
        </li>
        <li><strong>Problem sa serverom:</strong>
          <ul>
            <li>Provjerite status sustava</li>
            <li>Kontaktirajte podršku ako problem traje</li>
          </ul>
        </li>
      </ol>
    </details>

    <details>
      <summary>Gdje mogu vidjeti je li račun poslan kao eRacun?</summary>
      <p>Status eRacuna možete vidjeti:</p>
      <ul>
        <li>U popisu računa - kolona "eRacun" ili <span class="status-chip" style="background-color: #2196F3; color: white;">eRačun</span> chip</li>
        <li>U detaljima računa - sekcija "Način dostave"</li>
        <li>U izvještajima - filter po tipu dostave</li>
      </ul>
    </details>

    <details>
      <summary>Mogu li poslati eRacun za strane kupce (izvan RH)?</summary>
      <p>Ne, eRacun sustav trenutno podržava samo kupce iz Hrvatske:</p>
      <ul>
        <li>Država mora biti postavljena na "Hrvatska"</li>
        <li>Ograničenje dolazi od sustava eRacuna</li>
        <li>Za strane kupce koristite:</li>
        <ul>
          <li>Standardnu fiskalizaciju</li>
          <li>Ili slanje PDF računa putem standardnog emaila</li>
        </ul>
      </ul>
    </details>
  `
}

export default {
  id: 'settings',
  title: 'Postavke',
  icon: 'mdi-cog',
  show: true,
  order: 15,
  children: [
    {
      id: 'settings-users',
      title: 'Korisnici i privilegije',
      icon: 'mdi-account-multiple',
      show: true,
      order: 1,
      content: getSettingsUsersContent
    },
    {
      id: 'settings-certificates',
      title: 'Certifikati i fiskalizacija',
      icon: 'mdi-certificate',
      show: true,
      order: 2,
      content: getSettingsCertificatesContent
    },
    {
      id: 'settings-communication',
      title: 'Komunikacija i obavijesti',
      icon: 'mdi-bell',
      show: true,
      order: 3,
      content: getSettingsCommunicationContent
    },
    {
      id: 'settings-company',
      title: 'Postavke tvrtke',
      icon: 'mdi-office-building',
      show: true,
      order: 4,
      content: getSettingsCompanyContent
    },
    {
      id: 'settings-fiskalizacija-eracun',
      title: 'Fiskalizacija i eRacun',
      icon: 'mdi-receipt-text',
      show: true,
      order: 5,
      content: getSettingsFiskalizacijaEracunContent
    }
  ]
}
